import { useState } from "react";
import moment from "moment";
import type { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import type { EquipmentType } from "@freightsimple/generated-dashboard-openapi-client/models/EquipmentType";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import type { Quote } from "@freightsimple/generated-dashboard-openapi-client/models/Quote";
import type { SharedDedicated } from "@freightsimple/generated-dashboard-openapi-client/models/SharedDedicated";
import type { StateOfGoods } from "@freightsimple/generated-dashboard-openapi-client/models/StateOfGoods";
import type { UUID } from "@/Shared/types";
import { QuotesSortOptionsType } from "@/Shared/Quotes/Types/QuotesSortOptionsType";
import { BookShipmentQuoteLoadingBar } from "../CustomComponents/Internal/bookShipmentQuoteLoadingBar";
import { BookShipmentQuoteSortRow } from "../CustomComponents/Internal/bookShipmentQuoteSortRow";
import QuotesQuotingSuccessAnimation from "@/Shared/Quotes/CustomComponents/QuotesQuotingSuccessAnimation";
import { QuotesSort } from "@/Shared/Quotes/CustomComponents/QuotesSort";
import BookShipmentViewQuotes from "@/Features/BookShipment/CustomComponents/BookShipmentViewQuotes";

function sortQuotes(
  sortBy: QuotesSortOptionsType,
  quotes: readonly Quote[] | undefined,
): Quote[] | undefined {
  if (quotes === undefined) {
    return undefined;
  }

  return quotes.slice().sort(function (a, b) {
    if (sortBy === QuotesSortOptionsType.SortByLowestPrice) {
      return a.price - b.price;
    } else {
      // This is a total guess right now - but
      // assume that the mean of the distribution of transit times
      // is 30% into the window
      const averageTransitDaysA =
        ((a.latestTransitBusinessDays ?? 1000) +
          2 * (a.transitBusinessDays ?? 1000)) /
        3;
      const averageTransitDaysB =
        ((b.latestTransitBusinessDays ?? 1000) +
          2 * (b.transitBusinessDays ?? 1000)) /
        3;

      return averageTransitDaysA - averageTransitDaysB;
    }
  });
}

interface BookShipmentQuoteResultsProps {
  waitingForQuotes: boolean;
  quotes?: Readonly<Array<Quote>> | undefined;
  onModify: () => void;
  onBook: (quoteId: UUID) => void;
  pickupDate: moment.Moment;
  pickupLocation: PreQuoteLocation;
  deliveryLocation: PreQuoteLocation;
  deliveryDeadline: string | undefined;
  lineItems: Array<LineItem>;
  linearFeet: number | undefined;
  shipment: PreBookingShipment;
  addInsuranceToShipment: boolean;
  insuranceAmount: number;
  insuranceCurrency: Currency;
  stateOfGoods: StateOfGoods;
  equipmentType: EquipmentType | undefined;
  exclusiveUse: boolean;
  tarpRequired: boolean;
  onRequestManualQuotes: (
    shipmentId: UUID,
    equipmentType: EquipmentType,
    sharedDedicated: SharedDedicated,
    tarpRequired: boolean | undefined,
    linearFeet: number | undefined,
    notes: string,
  ) => Promise<void>;
}

export function BookShipmentQuoteResults(props: BookShipmentQuoteResultsProps) {
  const {
    waitingForQuotes,
    quotes,
    onBook,
    pickupDate,
    pickupLocation,
    deliveryDeadline,
    deliveryLocation,
    lineItems,
    linearFeet,
    onRequestManualQuotes,
    addInsuranceToShipment,
    insuranceAmount,
    insuranceCurrency,
    stateOfGoods,
    equipmentType,
    exclusiveUse,
    tarpRequired,
  } = props;

  const [wasWaitingForQuotes] = useState(waitingForQuotes);

  const [sortBy, setSortBy] = useState<QuotesSortOptionsType>(
    QuotesSortOptionsType.SortByLowestPrice,
  );

  // Deduplication and hidden-quote filtering is now handled server-side
  const sortedQuotes = sortQuotes(sortBy, quotes);

  return (
    <>
      {waitingForQuotes && <BookShipmentQuoteLoadingBar />}
      {wasWaitingForQuotes && !waitingForQuotes && (
        <QuotesQuotingSuccessAnimation />
      )}
      {sortedQuotes && (
        <>
          {sortedQuotes.length > 1 && (
            <BookShipmentQuoteSortRow>
              <QuotesSort sortBy={sortBy} setSortBy={setSortBy} />
            </BookShipmentQuoteSortRow>
          )}
          <BookShipmentViewQuotes
            pickupDeadline={props.shipment.pickupDeadline}
            shipmentId={props.shipment.shipmentId}
            pickupDate={moment(pickupDate)}
            pickupLocation={pickupLocation}
            lineItems={lineItems}
            linearFeet={linearFeet}
            deliveryLocation={deliveryLocation}
            onBook={onBook}
            quotes={sortedQuotes}
            sortBy={sortBy}
            // TODO: Do we need this?
            notes={undefined}
            onRequestManualQuotes={onRequestManualQuotes}
            addInsuranceToShipment={addInsuranceToShipment}
            insuranceAmount={insuranceAmount}
            insuranceCurrency={insuranceCurrency}
            stateOfGoods={stateOfGoods}
            equipmentType={equipmentType}
            exclusiveUse={exclusiveUse}
            tarpRequired={tarpRequired}
            deliveryDeadline={deliveryDeadline}
            pickupReferenceNumber={props.shipment.pickupReferenceNumber}
            deliveryReferenceNumber={props.shipment.deliveryReferenceNumber}
            deliveryAmazonReferenceId={props.shipment.deliveryAmazonReferenceId}
            deliveryAmazonShipmentId={props.shipment.deliveryAmazonShipmentId}
          />
        </>
      )}
    </>
  );
}
